import mixpanel from "mixpanel-browser";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import styled, { css, keyframes, useTheme } from "styled-components";
import { routeList } from "../App";
import { lang } from "../assets/languages/en.language";
import { storageKey } from "../configs/config";
import { useGeolocation } from "../hooks/useGeolocation";
import { usePageVisibility } from "../hooks/usePageVisibility";
import { usePwaViewportWidth } from "../hooks/useScreenWidth";
import api, { GetProfileData } from "../services/api";
import { randomizeList } from "../services/utils";
import { useUserStore } from "../store/useUserStore";
import { Button } from "../styles/Button.style";
import { MainContainer } from "../styles/Container.style";
import { Paragraph, Title } from "../styles/Text";
import { Column, Gap, Row } from "../styles/Utils";
import { MoreButton } from "./MoreButton.component.tsx";
import Popup from "./Popup.component";
import { QuestionsView } from "./QuestionsView.component.tsx";
import Slider from "./Slider.component";
import StatusBadge from "./StatusBadge.component";
import { BackIcon } from "./svgs/icons/Back.icon";
import { CheckMarkIcon } from "./svgs/icons/CheckMark.icon";
import { HearthIcon } from "./svgs/icons/Hearth.icon";
import LocationIcon from "./svgs/icons/Location.icon";

const animationDuration = 300;

export const Swiper: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const viewportWidth = usePwaViewportWidth();

  const {
    profile: { distance, isKmUnit },
    addMatch,
    setDogNameOnTopNavBar,
  } = useUserStore();
  const { location, getLocation } = useGeolocation();

  // Animation
  const [exitDirection, setExitDirection] = useState<"right" | "left" | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [profiles, setProfiles] = useState<GetProfileData[]>([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMoreInfosOpened, setIsMoreInfosOpened] = useState(false);
  const skipRef = useRef<number>(0);

  // Fetch next profiles with pagination
  const isFetchingRef = useRef<boolean>(false);
  const hasMoreProfilesRef = useRef<boolean>(true);

  // States
  const [displayStatutBadgeInfo, setDisplayStatutBadgeInfo] = useState<boolean>(false);

  // Important here to allow the user location to update on browser tab visibility
  usePageVisibility(() => {
    if (profiles.length == 0) {
      console.log(`Refreshing Swiper and Location on Browser Tab Focus`);
      sessionStorage.removeItem(storageKey.userLocation);
      // window.location.reload();
      getLocation();
      roundAgain();
    }
  });



  useEffect(() => {
    if (!location) {
      getLocation();
      return;
    }
    if (loading) {
      fetchProfiles();
    }
  }, [location]);

  useEffect(() => {
    console.log(`profiles.length - index's value: ${profiles.length - index}`, hasMoreProfilesRef.current);
    if (profiles.length - index <= 8 && hasMoreProfilesRef.current) {
      console.log("FETCH MORE PROFILES ######");
      // Will happen after roundAgain set the index to 0
      fetchProfiles();
    } else if (profiles.length - index == 0 && !hasMoreProfilesRef.current) {
      console.log("RELOAD ######");
      // window.location.reload();
      roundAgain();
    }
    // Update DogName on Top Nav Bar
    if (profiles.length > 0 && index <= profiles.length - 1) {
      setDogNameOnTopNavBar(profiles[index].dogName);
    }
  }, [index]);

  const fetchProfiles = async () => {
    if (!location || isFetchingRef.current || !hasMoreProfilesRef.current) return;

    isFetchingRef.current = true;
    try {
      const response = await api.getProfilesNearby(location.lat, location.lon, distance!, skipRef.current);
      const newProfiles = response.data;
      if (newProfiles.length > 0) {
        const randomizeProfileList = randomizeList(newProfiles);
        setProfiles((prev) => [...prev, ...randomizeProfileList]);
        if (skipRef.current == 0) {
          setDogNameOnTopNavBar(randomizeProfileList[index].dogName);
          // setDogNameOnTopNavBar("CHCOLATINE");
        }
        skipRef.current += newProfiles.length; // Incrément ici
      }
      // if api fecth less than 10 profiles, then no more profiles to fetch
      if (newProfiles.length < 10) {
        hasMoreProfilesRef.current = false;
      }
    } catch (err) {
      console.error("Error fetching profiles nearby", err);
      alert("Error fetching profiles nearby");
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  const roundAgain = () => {
    setDogNameOnTopNavBar(null);
    setLoading(true);
    setProfiles([]);
    setIndex(0);
    hasMoreProfilesRef.current = true;
    skipRef.current = 0;
  };

  const transition = (direction: "right" | "left") => {
    setExitDirection(direction);
    setIsTransitioning(true);
    setIsMoreInfosOpened(false);
    setTimeout(() => {
      setIndex(index + 1);
      setExitDirection(null);
      setIsTransitioning(false);
    }, animationDuration);
  };

  const likeProfile = async () => {
    try {
      const response = await api.vote({ targetedUserId: profiles[index]._id, isLikeVoting: true });
      transition("right");
      if (response.data.matchHappening) {
        mixpanel.track("Match");
        console.log("matchHappening");
        addMatch(response.data.targetedUserId);
        navigate(routeList.matchHappening);
      }
      mixpanel.track("Vote", { type: "Like" });
    } catch (err) {
      console.log("Error voting", err);
      alert("Error voting");
    }
  };

  const dislikeProfile = () => {
    try {
      api.vote({ targetedUserId: profiles[index]._id, isLikeVoting: false });
      transition("left");
      mixpanel.track("Vote", { properties: { type: "Dislike" } });
    } catch (err) {
      console.log("Error voting", err);
      alert("Error voting");
    }
  };

  if (!loading && index == profiles.length) {
    return (
      <Column $alignItem="center" $justifyContent="center" $maxHeight>
        <Gap $height={5} />
        <Title $color={theme.color.primaryDark}>No profile around</Title>
      </Column>
    );
  }

  if (loading) {
    return (
      <Column $alignItem="center" $justifyContent="center" $maxHeight>
        <Gap $height={5} />
        <Title $color={theme.color.primaryDark}>Loading profiles...</Title>
        <Gap $height={1} />
        <BeatLoader color={theme.color.primaryDark} />
      </Column>
    );
  }

  return (
    <>
      {displayStatutBadgeInfo && (
        <Popup
          type="info"
          title={lang.swiperPage.statutBadgeInfo.title}
          message={lang.swiperPage.statutBadgeInfo.message}
          buttonText={lang.swiperPage.statutBadgeInfo.buttonText}
          Icon={CheckMarkIcon}
          action={() => {
            setDisplayStatutBadgeInfo(false);
          }}
          onClose={() => {
            setDisplayStatutBadgeInfo(false);
          }}
        />
      )}
      {profiles.map((profile, i) => {
        const questions = profile.questions.filter((e) => e.enabled && e.answer != "");
        const distanceFromProfile = Math.floor(isKmUnit ? profile.distance! / 1000 : profile.distance! / 1600);
        return (
          <Column key={profile.email + profile.dogName} $alignItem="center" $maxHeight>
            {i == index && <Title>{profile.traits.join("-")}</Title>}
            <SwipperContainer>
              <MainContainerOvl
                $padding="1rem"
                $exitDirection={i === index ? exitDirection : null}
                $zIndex={profiles.length - i}
                style={{ display: i >= index ? "flex" : "none" }}
                $animationDuration={animationDuration}
              >
                <Slider relativeSizing isOverflowHidden buttonColor={theme.color.primary}>
                  {profile.imageUrls.map((img) => (
                    <Image key={img} src={img} onClick={() => setIsMoreInfosOpened(true)} />
                  ))}
                </Slider>
                <Gap $height={1} />
                <LocationIcon size={3} color={theme.color.primaryDark} />
                <Gap $height={0.5} />
                <Paragraph>
                  {distanceFromProfile} {isKmUnit ? "km" : "miles"} away
                </Paragraph>
                {profile.accountVerified && index == i && (
                  <StatutBadgeContainer>
                    <StatusBadge />
                  </StatutBadgeContainer>
                )}
                {isMoreInfosOpened && index == i && (
                  <>
                    <Gap $height={1} />
                    <QuestionsView questions={questions} />
                    <Gap $height={1} />
                    <MoreButton profileId={profile._id} />
                  </>
                )}
              </MainContainerOvl>
            </SwipperContainer>
          </Column>
        );
      })}
      <CTAContainer $justifyContent="center" $gap={8} $viewportWidth={viewportWidth} $isMoreInfosOpened={false}>
        <CtaBallButton onClick={dislikeProfile} disabled={isTransitioning}>
          <BackIcon color={theme.color.tertiaryLight} />
        </CtaBallButton>
        <CtaBallButton onClick={likeProfile} disabled={isTransitioning}>
          <HearthIcon color={theme.color.tertiary} />
        </CtaBallButton>
      </CTAContainer>
    </>
  );
};

export const StatutBadgeContainer = styled.div`
  position: absolute;
  right: -0.8rem;
  top: -0.8rem;
`;

const CTAContainer = styled(Row)<{ $viewportWidth: number; $isMoreInfosOpened: boolean }>`
  width: 100%;
  position: absolute;
  top: ${({ $viewportWidth, $isMoreInfosOpened }) => !$isMoreInfosOpened && $viewportWidth + "px"};
  bottom: ${({ $isMoreInfosOpened }) => $isMoreInfosOpened && 0};
  transform: ${(props) =>
    `translateX(-${props.theme.rootContainerInlinePadding}) translateY(calc(-50% - ${props.theme.rootContainerInlinePadding}))`};
  z-index: 99;
`;

const CtaBallButton = styled(Button)`
  width: 6rem;
  height: 6rem;
  border-radius: 100px;
  border: none;
  background: ${(props) => props.theme.color.primary};
  padding: 1.3rem;
  > svg {
    height: 100%;
  }
`;

type ExitDirection = "right" | "left";

const MainContainerOvl = styled(MainContainer)<{
  $exitDirection: ExitDirection | null;
  $zIndex: number;
  $animationDuration: number;
}>`
  position: absolute;
  height: unset;
  border: none;
  z-index: ${({ $zIndex }) => $zIndex};

  animation: ${({ $exitDirection, $animationDuration }) => {
    const fadeMap = {
      left: fadeOutLeft,
      right: fadeOutRight,
    };

    const animationName = $exitDirection ? fadeMap[$exitDirection] : null;

    if (!animationName) return "none";

    // ✅ Use css`` helper to inject keyframes properly
    return css`
      ${animationName} ${$animationDuration / 1000}s ease-in-out
    `;
  }};
`;

const SwipperContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Image = styled.img`
  border-radius: 30px;
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
`;

const fadeOutLeft = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(-100%);
    opacity: 0;
  }
`;

const fadeOutRight = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;
